<template>
    <div :class="{ paper: !disablePaper }">
        <div v-if="!disableTitle">
            <h2 class="headline  py-3 px-4 m-0">
                {{ $t('spotlight.education.dialog.addNewEducation') }}
            </h2>
            <Divider />
        </div>

        <div class="content px-4 py-4">
            <h6 class="subtitle bold">
                {{ $t('spotlight.education.dialog.institution') }}
            </h6>

            <BaseSearchable
                v-model="school"
                :data="schoolData"
                class="mb-4"
                creatable
                :placeholder="$t('spotlight.education.dialog.institution')"
                :helperText="schoolError"
                :error="schoolError !== null"
            />

            <div v-if="!!campuses.length">
                <h6 class="subtitle bold">
                    {{ $t('spotlight.education.dialog.campus') }}
                </h6>

                <BaseSearchable
                    v-model="campus"
                    :data="campusData"
                    class="mb-4"
                    :placeholder="$t('spotlight.education.dialog.campus')"
                    :helperText="schoolError"
                    :error="schoolError !== null"
                />
            </div>

            <div class="degree-info mb-4">
                <div class="w-100">
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.degreeType') }}
                    </h6>

                    <BaseSearchable
                        v-model="level"
                        :data="levels()"
                        :placeholder="$t('spotlight.education.dialog.degreeType')"
                        :helperText="levelError"
                        :error="levelError !== null"
                    />
                </div>

                <div class="w-100">
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.fieldOfStudy') }}
                    </h6>

                    <BaseSearchable
                        v-model="field"
                        :data="fields()"
                        :placeholder="$t('spotlight.education.dialog.fieldOfStudy')"
                        :helperText="fieldError"
                        :error="fieldError !== null"
                    />

                    <div
                        v-if="!isFieldValueOther"
                        class="notfindingfield mt-2"
                        href="#"
                        @click="setOtherField"
                    >
                        {{ $t('spotlight.education.dialog.cannotFindField') }}
                    </div>
                </div>
            </div>

            <TextField
                v-if="isFieldValueOther"
                v-model="degreeName"
                :label="$t('spotlight.education.dialog.fieldOfStudy')"
                :placeholder="$t('spotlight.education.dialog.fieldOfStudy')"
                margin
                :helperText="degreeError"
                :error="degreeError !== null"
            />

            <div class="date-content">
                <div>
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.studyStart') }}
                    </h6>
                    <YearMonthPicker
                        v-model="start"
                        :defaultText="$t('spotlight.education.dialog.selectStartDate')"
                    />
                </div>
                <div>
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.studyEnd') }}
                    </h6>
                    <YearMonthPicker
                        v-model="end"
                        :defaultText="$t('spotlight.education.dialog.selectEndDate')"
                    />
                </div>
            </div>
            <div v-for="(error, i) in [startError, endError]" :key="i">
                <h6 v-if="error" class="subtitle mt-2 error-color">
                    {{ error }}
                </h6>
            </div>

            <div v-if="!disableActions" class="d-flex flex-row-reverse mt-3">
                <BaseButton variant="secondary" @click="handleAction">
                    {{ degree ? $t('general.save') : $t('general.add') }}
                </BaseButton>

                <BaseButton class="mr-3" variant="text-secondary" @click="onClose">
                    {{ $t('general.close') }}
                </BaseButton>

                <div class="flex-grow" />

                <BaseButton v-if="degree" variant="text-error" @click="onDelete">
                    {{ $t('general.delete') }}
                </BaseButton>
            </div>
            <div v-else>
                <slot />
            </div>
        </div>
    </div>
</template>

<script>
// Utils
import degreeFields from 'highered-utils/data/degreeFields.json';
import degreeLevels from 'highered-utils/data/degreeLevels.json';

import OrganizationService from '~/services/OrganizationService';

export default {
    name: 'EducationModal',
    props: {
        degree: {
            // An existing degree
            type: Object,
            default: null,
        },
        disablePaper: {
            type: Boolean,
            default: false,
        },
        disableActions: {
            type: Boolean,
            default: false,
        },
        disableTitle: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        orgService: new OrganizationService(),

        campusId: null,
        campus: null,

        degreeName: '',
        school: null,
        start: null,
        end: null,
        field: null,
        level: null,
        degreeError: null,
        schoolError: null,
        startError: null,
        endError: null,
        fieldError: null,
        levelError: null,
        otherField: null,

        campuses: [],
    }),
    computed: {
        schools() {
            return this.$interfaces.candidate.school.get({ reactive: true });
        },
        isFieldValueOther() {
            return this.field && this.field.value === this.otherField.value;
        },
        schoolData() {
            return this.schools.data.map(s => ({ value: s.id, label: s.name }));
        },
        campusData() {
            return this.campuses.map(c => ({ value: c.id, label: c.name }));
        },
    },
    methods: {
        async getCampuses(schoolId) {
            const campuses = await this.orgService.getCampuses(schoolId).catch(err => {
                console.error(err);
                return [];
            });
            this.campuses = campuses;
        },
        fields() {
            return degreeFields.map(f => {
                const langFieldKey = `spotlight.education.dialog.diciplins.${f.value}`; // i18n key
                return {
                    ...f,
                    label: this.$te(langFieldKey) ? this.$t(langFieldKey) : f.label,
                };
            });
        },
        setOtherField() {
            this.field = this.otherField;
        },
        levels() {
            return degreeLevels.map(l => {
                const langDegreeKey = `spotlight.education.dialog.degrees.${l.value}`; // i18n key
                return {
                    ...l,
                    label: this.$te(langDegreeKey) ? this.$t(langDegreeKey) : l.label,
                };
            });
        },
        onClose() {
            this.clear();
            this.$emit('close');
        },
        clear() {
            this.degreeName = '';
            this.start = null;
            this.end = null;
            this.school = null;
            this.field = null;
            this.level = null;
            this.degreeError = null;
            this.schoolError = null;
            this.startError = null;
            this.endError = null;
            this.fieldError = null;
            this.levelError = null;
        },
        getInputData() {
            return {
                name: this.isFieldValueOther ? this.degreeName : '',
                school: this.school,
                start: this.start,
                end: this.end,
                school_id: (this.school || {}).value,
                field: { id: this.field.value, name: this.field.label },
                level: { id: this.level.value, name: this.level.label },
                field_id: this.field.value,
                level_id: this.level.value,
                campus_id: (this.campus || {}).value,
                campusId: (this.campus || {}).value,
            };
        },
        onAdd() {
            if (!this.isValidInput()) {
                return;
            }

            const data = this.getInputData();

            this.$emit('create', data);
            this.onClose();
        },
        onSave() {
            if (!this.isValidInput()) {
                return;
            }

            const data = {
                ...this.degree,
                ...this.getInputData(),
            };

            this.$emit('save', data);
            this.onClose();
        },
        onDelete() {
            this.$emit('delete', this.degree);
            this.onClose();
        },
        handleAction() {
            if (this.degree) {
                this.onSave();
            } else {
                this.onAdd();
            }
        },
        isValidInput() {
            // Validate input data
            if (!this.degreeName && this.isFieldValueOther) {
                this.degreeError = this.$t('spotlight.education.dialog.invalidDegreeField');
                return false;
            } else if (!this.school) {
                this.schoolError = this.$t('spotlight.education.dialog.invalidSchool');
                return false;
            } else if (!this.level) {
                this.levelError = this.$t('spotlight.education.dialog.invalidDegreeLevel');
                return false;
            } else if (!this.field) {
                this.fieldError = this.$t('spotlight.education.dialog.invalidDegreeField');
                return false;
            } else if (!this.start) {
                this.startError = this.$t('spotlight.education.dialog.invalidStartDate');
                return false;
            } else if (!this.end) {
                this.startError = this.$t('spotlight.education.dialog.invalidEndDate');
                return false;
            } else if (this.end < this.start) {
                this.startError = this.$t('spotlight.education.dialog.invalidStudyPeriod');
                return false;
            }
            return true;
        },
        mapPropsToData(newVal) {
            // newVal is a degree-object
            if (!newVal || !newVal.school) return;

            this.campusId = newVal.campusId;

            this.degreeName = newVal.name;
            this.school = {
                ...newVal.school,
                value: newVal.school.id,
                label: newVal.school.name,
            };

            const langFieldKey = `spotlight.education.dialog.diciplins.${newVal.field_id}`;
            const langLevelKey = `spotlight.education.dialog.degrees.${newVal.level_id}`;
            this.field = {
                value: newVal.field_id,
                label: this.$te(langFieldKey) ? this.$t(langFieldKey) : newVal.field.name,
            };
            this.level = {
                value: newVal.level_id,
                label: this.$te(langLevelKey) ? this.$t(langLevelKey) : newVal.level.name,
            };
            this.end = new Date(newVal.end);
            this.start = new Date(newVal.start);
        },
    },
    mounted() {
        const otherFieldId = '44c1f8cf-8696-4638-bc03-9763c2de32af';
        this.otherField = this.fields().find(f => f.value == otherFieldId);
        this.mapPropsToData(this.degree);
    },
    watch: {
        // Initializes values on degree props changed
        degree(newVal) {
            this.mapPropsToData(newVal);
        },
        degreeName() {
            this.degreeError = null;
        },
        school(value) {
            this.schoolError = null;
            if (!value) return;
            this.getCampuses(value.value);
        },
        campuses(allCampuses = []) {
            if (!allCampuses.length || !this.campusId) this.campus = null;
            else {
                const campusObj = allCampuses.find(c => c.id === this.campusId);
                this.campus = {
                    value: this.campusId,
                    label: campusObj.name,
                };
            }
        },
        start() {
            this.startError = null;
        },
        end() {
            this.endError = null;
        },
        field() {
            this.fieldError = null;
        },
        level() {
            this.levelError = null;
        },
    },
};
</script>

<style scoped>
.paper {
    width: 96%;
    max-width: 600px;
}
.date-content {
    display: grid;
    grid-template-columns: 1fr 1fr;

    grid-gap: 16px;
}

.degree-info {
    display: grid;
    grid-template-columns: 1fr 1fr;

    grid-gap: 16px;
}

.notfindingfield {
    cursor: pointer;
}

.notfindingfield:hover {
    color: var(--primary);
}

@media only screen and (max-width: 600px) {
    .degree-info {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr;
        grid-gap: 0px;
    }

    .paper {
        width: 100%;
        padding-bottom: 100px;
        border-radius: 0 !important;
    }

    .date-content {
        grid-template-columns: 1fr;
    }
}
</style>
